import React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ContactForm from "../../components/ContactForm";

const ContactUsPage = () => {
  const { t, i18n } = useTranslation("contact_us");
  const lang = (i18n?.language || "ar").startsWith("ar") ? "ar" : "en";

  const contactInfo = [
    {
      icon: <EmailIcon sx={{ fontSize: 32, color: "#d5aa2f" }} />,
      label: lang === "ar" ? "البريد الإلكتروني" : "Email",
      value: "info@didastore.com",
      link: "mailto:info@didastore.com",
    },
    {
      icon: <PhoneIcon sx={{ fontSize: 32, color: "#d5aa2f" }} />,
      label: lang === "ar" ? "الهاتف" : "Phone",
      value: "+20 123 456 7890",
      link: "tel:+201234567890",
    },
    {
      icon: <LocationOnIcon sx={{ fontSize: 32, color: "#d5aa2f" }} />,
      label: lang === "ar" ? "الموقع" : "Location",
      value: lang === "ar" ? "البحيرة، مصر" : "Cairo, Egypt",
      link: null,
    },
  ];

  const socialLinks = [
    {
      icon: <FacebookIcon />,
      label: "Facebook",
      href: "https://www.facebook.com/yourpage",
    },
    {
      icon: <InstagramIcon />,
      label: "Instagram",
      href: "https://www.instagram.com/yourprofile",
    },
    {
      icon: <TwitterIcon />,
      label: "Twitter",
      href: "https://twitter.com/yourprofile",
    },
    {
      icon: <WhatsAppIcon />,
      label: "WhatsApp",
      href: "https://wa.me/201234567890",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)",
        py: 6,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            fontWeight={800}
            sx={{
              background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              mb: 2,
            }}
          >
            {lang === "ar" ? "تواصل معنا" : "Contact Us"}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#666",
              fontWeight: 300,
              maxWidth: "600px",
              mx: "auto",
            }}
          >
            {lang === "ar" 
              ? "نحن هنا للإجابة على جميع استفساراتك. تواصل معنا للحصول على المساعدة والدعم"
              : "We're here to answer all your questions. Contact us for help and support"}
          </Typography>
        </Box>

        {/* Contact Info Cards */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          {contactInfo.map((info, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  background: "#fff",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 8px 24px rgba(213, 170, 47, 0.15)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                    {info.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{
                      textAlign: "center",
                      color: "#1a1a1a",
                      mb: 1,
                    }}
                  >
                    {info.label}
                  </Typography>
                  {info.link ? (
                    <Typography
                      component="a"
                      href={info.link}
                      sx={{
                        textAlign: "center",
                        color: "#d5aa2f",
                        textDecoration: "none",
                        fontWeight: 600,
                        display: "block",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          color: "#e6c76a",
                        },
                      }}
                    >
                      {info.value}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "center",
                        color: "#666",
                        fontWeight: 600,
                      }}
                    >
                      {info.value}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Main Content - Form and Info */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {/* Contact Form */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                background: "#fff",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                p: 3,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  color: "#1a1a1a",
                  mb: 3,
                }}
              >
                {lang === "ar" ? "أرسل رسالة" : "Send a Message"}
              </Typography>
              <ContactForm />
            </Card>
          </Grid>

          {/* Additional Info */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              {/* Hours of Operation */}
              <Card
                sx={{
                  background: "#fff",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  p: 3,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    color: "#1a1a1a",
                    mb: 2,
                  }}
                >
                  {lang === "ar" ? "ساعات العمل" : "Hours of Operation"}
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    <strong>{lang === "ar" ? "السبت - الخميس:" : "Saturday - Thursday:"}</strong> {lang === "ar" ? "9:00 صباحاً - 6:00 مساءً" : "9:00 AM - 6:00 PM"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666" }}>
                    <strong>{lang === "ar" ? "الجمعة:" : "Friday:"}</strong> {lang === "ar" ? "3:00 مساءً - 9:00 مساءً" : "3:00 PM - 9:00 PM"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#999", fontSize: "0.85rem", mt: 1 }}>
                    {lang === "ar" 
                      ? "وقت الرد المتوقع: خلال 24 ساعة"
                      : "Expected Response Time: Within 24 hours"}
                  </Typography>
                </Stack>
              </Card>

              {/* Social Media */}
              <Card
                sx={{
                  background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  p: 3,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    color: "#d5aa2f",
                    mb: 2,
                  }}
                >
                  {lang === "ar" ? "تابعنا على وسائل التواصل" : "Follow Us on Social Media"}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
                  {socialLinks.map((social, index) => (
                    <IconButton
                      key={index}
                      component="a"
                      href={social.href}
                      target="_blank"
                      rel="noopener"
                      aria-label={social.label}
                      sx={{
                        bgcolor: "rgba(213, 170, 47, 0.1)",
                        color: "#d5aa2f",
                        border: "1px solid rgba(213, 170, 47, 0.2)",
                        "&:hover": {
                          bgcolor: "#d5aa2f",
                          color: "#fff",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(213, 170, 47, 0.3)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Stack>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        {/* FAQ or Additional Info */}
        <Card
          sx={{
            background: "#fff",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            p: 4,
          }}
        >
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              color: "#1a1a1a",
              mb: 3,
            }}
          >
            {lang === "ar" ? "الأسئلة الشائعة" : "Frequently Asked Questions"}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#d5aa2f", mb: 1 }}>
                {lang === "ar" ? "كم وقت الشحن؟" : "What is the shipping time?"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
                {lang === "ar" 
                  ? "الشحن يستغرق من 2-5 أيام عمل حسب الموقع"
                  : "Shipping takes 2-5 business days depending on location"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#d5aa2f", mb: 1 }}>
                {lang === "ar" ? "هل يمكن استرجاع المنتجات؟" : "Can I return products?"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
                {lang === "ar" 
                  ? "نعم، يمكن استرجاع المنتجات خلال 30 يوم"
                  : "Yes, products can be returned within 30 days"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#d5aa2f", mb: 1 }}>
                {lang === "ar" ? "ما طرق الدفع المتاحة؟" : "What payment methods are available?"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                {lang === "ar" 
                  ? "نقبل جميع طرق الدفع الإلكترونية والدفع عند الاستلام"
                  : "We accept all electronic payment methods and cash on delivery"}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#d5aa2f", mb: 1 }}>
                {lang === "ar" ? "هل توجد ضمان على المنتجات؟" : "Is there a warranty on products?"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#666" }}>
                {lang === "ar" 
                  ? "نوفر ضمان الجودة والاستبدال للمنتجات التالفة"
                  : "We provide quality assurance and replacement for damaged products"}
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </Box>
  );
};

export default ContactUsPage;
